"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { WrapperOuter } from "./molecules";
import ClientSideLatLong from "./ClientSideLatLong";
import CaptureClickId from "./CaptureClickId";
import { Providers } from "../app/[lang]/nextUiProvider"; 
import DictionaryProvider from "../context/dictionaryProvider";
import SessionProvider from "../app/[lang]/nextAuthSessionProvider"; 
import { usePathname } from "next/navigation";
import { stickyFooterCourseStorageKey } from "../lib/util";
import EngageXIdentity from "./EngageXIdentity";
import TrackingAuto from "./TrackingAuto";
import EngageXOnsite from "./EngageXOnsite";

const Header = dynamic(() => import("./Header/Header"), { ssr: true });
const Footer = dynamic(() => import("./Footer"), { ssr: false });

export default function FullLayout({ children, dictionary, dir }) {
  const [showFooter, setShowFooter] = useState(false);
  const pathname = usePathname(); // get current URL path
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  const pathSegments = normalizedPath.split("/").filter(Boolean);
  const hideHeaderFooter = pathname.includes("maan") || pathname.includes("workshops"); // hide on focused landing pages
  const isBlogPage = pathname.includes("/blog");
  const isHomePage = pathname === `/${dir}` || pathname === "/";
  const isCorporateTrainingPage = pathname.includes("/corporate-trainings");
  const reservedTopLevelRoutes = new Set([
    "blog",
    "browsecourses",
    "category_type",
    "corporate-trainings",
    "testimonials",
    "pay-now",
    "bookings",
    "p",
  ]);
  const isTopLevelCourseSlugPage =
    ((pathSegments.length === 2 &&
      pathSegments[0] === dir &&
      !reservedTopLevelRoutes.has(pathSegments[1])) ||
      (pathSegments.length === 1 &&
        !reservedTopLevelRoutes.has(pathSegments[0])));
  const isCoursesPage =
    pathname.includes("/browsecourses") ||
    pathname.includes("/category_type") ||
    pathname.includes("/p/") ||
    isTopLevelCourseSlugPage;
  const showOtherFooterStickyCta =
    !isCorporateTrainingPage &&
    !isBlogPage &&
    (isHomePage || isCoursesPage);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isCoursesPage) {
      localStorage.removeItem(stickyFooterCourseStorageKey);
    }
  }, [isCoursesPage]);

  useEffect(() => {
    if (hideHeaderFooter) return;

    const renderFooter = () => {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(() => setShowFooter(true));
        return;
      }
      window.setTimeout(() => setShowFooter(true), 800);
    };

    if (document.readyState === "complete") {
      renderFooter();
      return;
    }

    window.addEventListener("load", renderFooter, { once: true });
    return () => window.removeEventListener("load", renderFooter);
  }, [hideHeaderFooter]);

  return (
    <>
      <SessionProvider>
        <Providers>
          <DictionaryProvider dictionary={{ ...dictionary, dir }}>
            <WrapperOuter>
              <ClientSideLatLong />
              <CaptureClickId />
              <EngageXIdentity />
              <TrackingAuto />
              {!hideHeaderFooter && (
                <>
                  <Header />
                  <div className="headerSpacer" aria-hidden="true" />
                </>
              )}
              <main className="main">{children}</main>
              <EngageXOnsite />
              {!hideHeaderFooter && showFooter && <Footer />}
            </WrapperOuter>
          </DictionaryProvider>
        </Providers>
      </SessionProvider>
    </>
  );
}
